import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, Star } from "lucide-react";
import { fetchBlogComments, fetchBlogAggregateRating, submitBlogComment } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * Reader reflections beneath the essay. Anyone can submit; nothing shows
 * until an admin approves it in /admin/blog. Includes a honeypot `company`
 * field that bots fill and humans never see.
 */
export default function Comments({ postSlug }) {
  const [comments, setComments] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    author_name: "",
    author_email: "",
    body: "",
    rating: 0,
    company: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [res, agg] = await Promise.all([
          fetchBlogComments(postSlug),
          fetchBlogAggregateRating(postSlug).catch(() => null),
        ]);
        if (!cancelled) {
          setComments(res.comments || []);
          setAggregate(agg);
        }
      } catch {
        if (!cancelled) setComments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [postSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.body.trim() || !form.author_name.trim() || !form.author_email.trim()) return;
    try {
      setBusy(true);
      await submitBlogComment({
        post_slug: postSlug,
        author_name: form.author_name,
        author_email: form.author_email,
        body: form.body,
        rating: form.rating > 0 ? form.rating : null,
        company: form.company,
      });
      setSubmitted(true);
      setForm({ author_name: "", author_email: "", body: "", rating: 0, company: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post your reflection. Try again?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-16 border-t border-obsidian-600 pt-10"
      aria-labelledby="comments-heading"
      data-testid="blog-comments"
    >
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <MessageCircle className="h-4 w-4 text-terracotta" aria-hidden="true" />
        <h2
          id="comments-heading"
          className="font-serif text-2xl text-bone-100"
        >
          Reflections
          {comments.length > 0 && (
            <span className="font-sans text-xs uppercase tracking-[0.24em] text-bone-500 ml-3">
              · {comments.length}
            </span>
          )}
        </h2>
      </div>

      {aggregate && aggregate.count > 0 && (
        <div
          className="flex items-center gap-3 mb-6 text-bone-200 font-sans text-sm"
          data-testid="blog-aggregate-rating"
          aria-label={`Average rating: ${aggregate.avg} out of 5, based on ${aggregate.count} reviews`}
        >
          <div className="flex items-center gap-0.5" aria-hidden="true">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                className={`h-4 w-4 ${
                  n <= Math.round(aggregate.avg)
                    ? "text-terracotta fill-terracotta"
                    : "text-bone-500"
                }`}
              />
            ))}
          </div>
          <span className="font-serif italic text-bone-300">
            {aggregate.avg.toFixed(1)} · {aggregate.count} reader{aggregate.count === 1 ? "" : "s"}
          </span>
        </div>
      )}

      {loading ? (
        <p className="font-sans text-bone-500 text-sm">Loading…</p>
      ) : comments.length === 0 ? (
        <p className="font-serif italic text-bone-500 mb-8">
          No reflections yet — yours can be the first.
        </p>
      ) : (
        <ul className="space-y-6 mb-10 list-none p-0" data-testid="blog-comments-list">
          {comments.map((c) => (
            <li
              key={c.id}
              className="border-l-2 border-terracotta/50 pl-4 py-1"
              data-testid={`blog-comment-${c.id}`}
            >
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500">
                  <span className="text-bone-300">{c.author_name}</span>
                  <span> · {formatDate(c.created_at)}</span>
                </p>
                {c.rating && (
                  <div
                    className="flex items-center gap-0.5"
                    aria-label={`${c.author_name} rated this ${c.rating} out of 5`}
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={`h-3 w-3 ${
                          n <= c.rating
                            ? "text-terracotta fill-terracotta"
                            : "text-bone-500"
                        }`}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                )}
              </div>
              <p className="font-serif text-bone-200 text-[1.05rem] leading-relaxed whitespace-pre-wrap">
                {c.body}
              </p>
            </li>
          ))}
        </ul>
      )}

      {submitted ? (
        <div
          className="border border-terracotta bg-terracotta/10 p-5"
          data-testid="blog-comment-success"
          role="status"
        >
          <p className="font-serif italic text-bone-100">
            Thanks — your reflection is with the moderator. Once approved it will appear here,
            and the most resonant reflections earn a quiet spot on the Umbra landing page.
          </p>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="border border-obsidian-600 bg-obsidian-800/30 p-5 sm:p-6 space-y-4"
          data-testid="blog-comment-form"
        >
          <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta">
            Leave a reflection
          </p>
          {/* Honeypot — hidden from humans, filled by bots */}
          <div style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
            <label htmlFor="company">Company</label>
            <input
              id="company"
              type="text"
              tabIndex="-1"
              autoComplete="off"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="c-name" className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
                Name
              </label>
              <input
                id="c-name"
                type="text"
                required
                autoComplete="name"
                maxLength={80}
                value={form.author_name}
                onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                data-testid="blog-comment-name"
                className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50"
                style={{ fontSize: "16px" }}
              />
            </div>
            <div>
              <label htmlFor="c-email" className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
                Email (not published)
              </label>
              <input
                id="c-email"
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                value={form.author_email}
                onChange={(e) => setForm({ ...form, author_email: e.target.value })}
                data-testid="blog-comment-email"
                className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50"
                style={{ fontSize: "16px" }}
              />
            </div>
          </div>
          <div>
            <p className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 block mb-2">
              Star this essay (optional)
            </p>
            <div
              role="radiogroup"
              aria-label="Rate this essay from 1 to 5 stars"
              className="flex items-center gap-1"
              data-testid="blog-comment-rating"
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={form.rating === n}
                  aria-label={`${n} star${n === 1 ? "" : "s"}`}
                  onClick={() => setForm((f) => ({ ...f, rating: f.rating === n ? 0 : n }))}
                  data-testid={`blog-comment-star-${n}`}
                  className="p-1 hover:scale-110 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
                >
                  <Star
                    className={`h-6 w-6 ${
                      n <= form.rating
                        ? "text-terracotta fill-terracotta"
                        : "text-bone-500 hover:text-terracotta"
                    }`}
                  />
                </button>
              ))}
              {form.rating > 0 && (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, rating: 0 }))}
                  className="ml-2 font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta"
                  data-testid="blog-comment-star-clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div>
            <label htmlFor="c-body" className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
              Your reflection
            </label>
            <textarea
              id="c-body"
              required
              rows={5}
              maxLength={2000}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="blog-comment-body"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-serif text-[0.95rem] leading-relaxed focus:outline-none focus:ring-2 focus:ring-terracotta/50"
              style={{ fontSize: "16px" }}
            />
            <p className="font-sans text-[0.6rem] text-bone-500 mt-1 italic">
              Moderated. Kind, curious, and lived-experience welcome. Anything else won't make it through.
              {" "}The most resonant reflections may be featured on the Umbra landing page.
            </p>
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="blog-comment-submit"
            className="bg-terracotta hover:bg-terracotta-deep disabled:opacity-60 text-obsidian-900 font-sans uppercase tracking-[0.24em] text-xs px-6 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post reflection"}
          </button>
        </form>
      )}
    </section>
  );
}
